import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

function AboutPage() {
  return (
    <div className="bg-gradient-to-b from-slate-900 to-slate-800 text-white">
      <Helmet>
        <title>About CommunityGuardAPI — Public Safety Data Infrastructure</title>
        <link rel="canonical" href="https://communityguardapi.com/about" />
        <meta name="description" content="CommunityGuardAPI aggregates sex offender registry data from all 50 states into a single REST API. Built by Asan Digital." />
        <meta property="og:title" content="About CommunityGuardAPI — Public Safety Data Infrastructure" />
        <meta property="og:description" content="CommunityGuardAPI aggregates sex offender registry data from all 50 states into a single REST API. Built by Asan Digital." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://communityguardapi.com/about" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      {/* Hero */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 text-center">
        <div className="max-w-3xl mx-auto">
          <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-500 font-semibold mb-4">About CommunityGuardAPI</p>
          <h1 className="text-3xl sm:text-5xl font-bold leading-tight mb-6">
            Public data infrastructure for <span className="text-emerald-500">community safety</span>
          </h1>
          <p className="text-base sm:text-lg text-slate-400 leading-relaxed max-w-2xl mx-auto">
            CommunityGuardAPI aggregates, standardizes, and serves sex offender registry data from all 50 states through a single REST API. Built for developers, schools, and civic organizations that need reliable access to public safety information.
          </p>
        </div>
      </section>

      {/* Origin Story */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 border-t border-slate-800">
        <div className="max-w-5xl mx-auto grid sm:grid-cols-2 gap-8 sm:gap-16 items-center">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Origin</p>
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">Born from a real problem</h2>
            <p className="text-slate-400 leading-relaxed mb-4">
              While building <a href="https://eduqueue.com" target="_blank" rel="noopener noreferrer" className="text-emerald-500 hover:text-emerald-400 transition-colors">EduQueue</a>, a K-12 visitor management system, we needed to screen visitors against sex offender registries. The problem: no unified, developer-friendly data source existed.
            </p>
            <p className="text-slate-400 leading-relaxed">
              State registries are fragmented — different formats, different access methods, inconsistent data. So we built the infrastructure ourselves, then opened it up as an API for anyone who needs it.
            </p>
          </div>
          <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-6 sm:p-8">
            <div className="space-y-5">
              {[
                { num: '50', label: 'State registries unified' },
                { num: '580K+', label: 'Records standardized' },
                { num: '1', label: 'Simple REST API' },
              ].map((s) => (
                <div key={s.label} className="flex items-baseline gap-4">
                  <span className="text-3xl sm:text-4xl font-bold text-emerald-500 tabular-nums min-w-[80px]">{s.num}</span>
                  <span className="text-sm text-slate-400">{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What we do */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 border-t border-slate-800">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">What we provide</p>
            <h2 className="text-2xl sm:text-3xl font-bold">Data infrastructure, not just a database</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-4 sm:gap-6">
            {[
              {
                icon: (
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125" />
                  </svg>
                ),
                title: 'Aggregation',
                desc: 'We pull from every state registry, normalize field names, geocode addresses, and deliver clean JSON.',
              },
              {
                icon: (
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                  </svg>
                ),
                title: 'Geospatial search',
                desc: 'Search by coordinates, radius, or ZIP code. Get distance-sorted results with lat/lng for every record.',
              },
              {
                icon: (
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" />
                  </svg>
                ),
                title: 'Developer-first API',
                desc: 'Standard REST endpoints, API key auth, paginated responses. Integrate in minutes, not weeks.',
              },
            ].map((item) => (
              <div key={item.title} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5 sm:p-6">
                <div className="text-emerald-500 mb-3">{item.icon}</div>
                <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 border-t border-slate-800">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Built for</p>
            <h2 className="text-2xl sm:text-3xl font-bold">Teams that need reliable registry data</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-4 sm:gap-6">
            {[
              {
                emoji: '🏫',
                title: 'Schools & Districts',
                desc: 'Screen visitors, contractors, and volunteers against registry data before they enter campus.',
              },
              {
                emoji: '💻',
                title: 'Developers',
                desc: 'Build safety features into your own applications with a clean, documented REST API.',
              },
              {
                emoji: '🏛️',
                title: 'Civic Organizations',
                desc: 'Support community safety programs with reliable, up-to-date registry information.',
              },
            ].map((item) => (
              <div key={item.title} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5 sm:p-6 text-center">
                <div className="text-3xl mb-3">{item.emoji}</div>
                <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Asan Digital / Ecosystem */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 border-t border-slate-800">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Ecosystem</p>
            <h2 className="text-2xl sm:text-3xl font-bold">Part of the Asan Digital platform</h2>
            <p className="text-slate-400 mt-3 max-w-xl mx-auto">
              CommunityGuardAPI is built and maintained by <a href="https://asan.digital" target="_blank" rel="noopener noreferrer" className="text-emerald-500 hover:text-emerald-400 transition-colors">Asan Digital LLC</a>. It powers registry screening across our product suite.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 max-w-2xl mx-auto">
            {[
              {
                name: 'EduQueue',
                href: 'https://eduqueue.com',
                desc: 'K-12 visitor management with automated registry screening at check-in.',
              },
              {
                name: 'Visitozy',
                href: 'https://www.visitozy.com',
                desc: 'Enterprise visitor management with digital check-ins and security workflows.',
              },
            ].map((product) => (
              <a
                key={product.name}
                href={product.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-slate-800/50 border border-slate-700/50 hover:border-emerald-500/30 rounded-xl p-5 sm:p-6 transition-colors"
              >
                <h3 className="text-white font-semibold mb-2 group-hover:text-emerald-400 transition-colors">
                  {product.name}
                  <span className="text-slate-600 ml-2 text-xs">↗</span>
                </h3>
                <p className="text-sm text-slate-400 leading-relaxed">{product.desc}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 border-t border-slate-800">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">Ready to integrate?</h2>
          <p className="text-slate-400 mb-8 max-w-lg mx-auto">
            Start with 100 free API calls per month. No credit card, no contracts.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/signup"
              className="px-8 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-lg shadow-lg transition text-center"
            >
              Create Free Account
            </Link>
            <Link
              to="/api-docs"
              className="px-8 py-3 border border-slate-600 text-slate-300 hover:text-white hover:border-slate-500 rounded-lg font-medium transition text-center"
            >
              Read the Docs
            </Link>
          </div>
          <p className="text-xs text-slate-600 mt-6">
            Questions? <a href="mailto:info@asan.digital" className="text-slate-500 hover:text-emerald-400 transition-colors">info@asan.digital</a>
          </p>
        </div>
      </section>
    </div>
  );
}

export default AboutPage;
